// Finance Settings Storage — Task 23
//
// Company-wide finance settings stored in localStorage.
// Phase 1: localStorage-backed (no backend required).
// Phase 2: replace read/write calls with:
//   GET  /api/settings/finance  → load on mount
//   PATCH /api/settings/finance → save on user action
//
// The interface stays identical between phases — only the storage layer changes.

import type { GpCalculationMethod } from './gp-calculation';

export interface FinanceSettings {
  /**
   * GP Calculation Method — determines how Gross Profit and Gross Margin %
   * are calculated across all Financials tab cards, charts, and tables.
   *
   * 'simple'       — Revenue minus all direct costs (default)
   * 'contribution' — Revenue minus Labour and Subcontractors only
   * 'fully-loaded' — Revenue minus direct costs plus overhead absorption
   */
  gpCalculationMethod: GpCalculationMethod;

  /** Last updated ISO timestamp (stamped automatically on save) */
  lastUpdated?: string;
}

const STORAGE_KEY = 'finance-settings';

export const DEFAULT_FINANCE_SETTINGS: FinanceSettings = {
  gpCalculationMethod: 'simple',
};

/**
 * Load finance settings from localStorage.
 * Returns defaults if nothing has been saved yet.
 */
export function getFinanceSettings(): FinanceSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<FinanceSettings>;
      return { ...DEFAULT_FINANCE_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Error loading finance settings:', error);
  }
  return { ...DEFAULT_FINANCE_SETTINGS };
}

/**
 * Save finance settings to localStorage.
 */
export function saveFinanceSettings(settings: FinanceSettings): void {
  try {
    const toSave: FinanceSettings = {
      ...settings,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving finance settings:', error);
    throw error;
  }
}
